/**
 * Funder constants and utilities for the AXIS referral system
 *
 * These constants define the ACC funder types and provide helper functions
 * to determine if a funder is ACC-related. This is used for:
 * - Conditionally showing/hiding ACC claim number and ACC status fields
 * - Determining if ACC fields are required for completeness
 *
 * IMPORTANT: Keep in sync with database trigger in:
 * database/migrations/049_funder_aware_completeness.sql
 *
 * ACC funder types require ACC claim number and ACC validation status.
 * Non-ACC funder types (Private, Southern Cross, Public) do not.
 *
 * Note: ICP MSK is excluded from ACC funders as it follows a distinct workflow
 * and does not require ACC claim number validation via the standard trigger.
 */

/**
 * ACC funder types that require ACC claim number and validation status.
 * These funders must have acc_claim_number and acc_number_validated
 * for the referral to be considered complete.
 *
 * IMPORTANT: These values must exactly match the funder values stored in the
 * database (including the en-dash in "ACC – Careway", etc.)
 */
export const ACC_FUNDER_TYPES = [
  'ACC',
  'ACC – Careway',
  'ACC – Accredited Employer',
  'ACC – RESS',
] as const;

/**
 * All supported funder types (ACC and non-ACC)
 */
export const ALL_FUNDER_TYPES = [
  'ACC',
  'ACC – Careway',
  'ACC – Accredited Employer',
  'ACC – RESS',
  'ICP MSK',
  'Private',
  'Southern Cross',
  'Public',
] as const;

/**
 * Type for ACC funder values
 */
export type AccFunderType = typeof ACC_FUNDER_TYPES[number];

/**
 * Type for all funder values
 */
export type FunderType = typeof ALL_FUNDER_TYPES[number];

/**
 * Checks if a funder value is an ACC type that requires ACC claim number
 * and validation status for completeness.
 *
 * @param funder - The funder value to check
 * @returns true if the funder is an ACC type, false otherwise
 *
 * @example
 * isAccFunder('ACC') // true
 * isAccFunder('ACC – Careway') // true
 * isAccFunder('Private') // false
 * isAccFunder(null) // false
 */
export function isAccFunder(funder: string | null | undefined): boolean {
  if (!funder) return false;
  return ACC_FUNDER_TYPES.includes(funder as AccFunderType);
}

/**
 * Gets all funder options for dropdown selects.
 * Returns an array of { value, label } objects suitable for select components.
 */
export function getFunderOptions(): { value: FunderType; label: string }[] {
  return [
    { value: 'ACC', label: 'ACC' },
    { value: 'ACC – Careway', label: 'ACC – Careway' },
    { value: 'ACC – Accredited Employer', label: 'ACC – Accredited Employer' },
    { value: 'ACC – RESS', label: 'ACC – RESS' },
    { value: 'ICP MSK', label: 'ICP MSK' },
    { value: 'Private', label: 'Private' },
    { value: 'Southern Cross', label: 'Southern Cross' },
    { value: 'Public', label: 'Public' },
  ];
}

/**
 * Gets the display label for a funder value.
 *
 * @param funder - The funder value
 * @returns The display label, or the original value if not found
 */
export function getFunderLabel(funder: string | null | undefined): string {
  if (!funder) return 'Not Set';
  const option = getFunderOptions().find(opt => opt.value === funder);
  return option?.label || funder;
}
